import React from "react";
import PropTypes from "prop-types";
import {
  getClaimsByInstitutionOutcome,
  getClaimsByInstitutionOutcomeAndGround,
  getClaimsByOutcomeAtInstitution,
  getClaimsByOutcomeAndGroundAtInstitution,
  getClaimsByPetitionerLawfirmInstitutionOutcome,
  getClaimsByPatentOwnerLawfirmInstitutionOutcome,
  getClaimsByAuthorJudgeInstitutionOutcome,
  getClaimsByPanelJudgeInstitutionOutcome,
  getClaimsByPetitionerLawfirmFWDOutcome,
  getClaimsByPatentOwnerLawfirmFWDOutcome,
  getClaimsByAuthorJudgeFWDOutcome,
  getClaimsByPanelJudgeFWDOutcome
} from "@/services/api-ptab";
import { DonutChart, StackedBarChart, makeWidthFlexible } from "@unifiedpatents/euclid-react";
import { nestedObjectsToGroupedSeries, objectToSeries, orderBy } from "@unifiedpatents/euclid-react/lib/helpers/preprocessors";
import * as Colors from "@unifiedpatents/euclid-react/lib/helpers/colors";
import ChartWithControls from "@/components/common/chart-with-controls";

const COLOR_MAP = {
  Instituted: Colors.blue400,
  Unpatentable: Colors.blue400,
  Cancelled: Colors.blue300,
  Settled: Colors.blue100,
  Patentable: Colors.grey500,
  Denied: Colors.grey400,
  "Not Instituted": Colors.grey400,
  Pending: Colors.orange300
};

const MARGIN_LEFT = { left: 160, right: 50, top: 50, bottom: 50 };
const MARGIN_MORE_LEFT = { left: 280, right: 50, top: 50, bottom: 50 };

const FlexibleDonutChart = makeWidthFlexible(DonutChart);
const FlexibleStackedBarChart = makeWidthFlexible(StackedBarChart);

const propValidation = { query: PropTypes.object };

export const ClaimsByInstitutionOutcomeChart = props => {
  const dataFormatter = data => orderBy(objectToSeries(data), ["Denied", "Instituted"]);
  return (
    <ChartWithControls
      apiCall={getClaimsByInstitutionOutcome}
      query={props.query}
      chart={FlexibleDonutChart}
      dataFormatter={dataFormatter}
      title="Institutions for Challenged Claims"
      colorMap={COLOR_MAP}
      scrollable={false}
      {...props}
    />
  );
};
ClaimsByInstitutionOutcomeChart.propTypes = propValidation;

export const ClaimsByGroundsAndInstitutionOutcomeChart = props => (
  <ChartWithControls
    apiCall={getClaimsByInstitutionOutcomeAndGround}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "percentage", sort: ["102", "102 & 103", "103"] }}
    title="Institutions for Challenged Claims"
    subtitle="by Invalidity (102 & 103) Grounds"
    axisTitleX="Petitioner Asserted Grounds"
    colorMap={COLOR_MAP}
    tickTotalY={5}
    legends={["Denied", "Instituted"]}
    scrollable={false}
    {...props}
  />
);
ClaimsByGroundsAndInstitutionOutcomeChart.propTypes = propValidation;

export const ClaimsByOutcomeAtInstitutionChart = props => {
  const dataFormatter = data => orderBy(objectToSeries(data), ["Patentable", "Pending", "Settled", "Cancelled", "Unpatentable"]);
  return (
    <ChartWithControls
      apiCall={getClaimsByOutcomeAtInstitution}
      query={props.query}
      chart={FlexibleDonutChart}
      dataFormatter={dataFormatter}
      title="Outcomes for Instituted Claims"
      colorMap={COLOR_MAP}
      scrollable={false}
      {...props}
    />
  );
};
ClaimsByOutcomeAtInstitutionChart.propTypes = propValidation;

export const ClaimsByGroundsAndOutcomeAtInstitutionChart = props => (
  <ChartWithControls
    apiCall={getClaimsByOutcomeAndGroundAtInstitution}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "percentage", sort: ["102", "102 & ~103", "102 & 103", "~102 & 103", "103"] }}
    title="Outcomes for Instituted Claims"
    subtitle="by Invalidity (102 & 103) Grounds"
    axisTitleX="Board Instituted Grounds"
    colorMap={COLOR_MAP}
    tickTotalY={5}
    legends={["Patentable", "Pending", "Settled", "Cancelled", "Unpatentable"]}
    scrollable={false}
    {...props}
  />
);
ClaimsByGroundsAndOutcomeAtInstitutionChart.propTypes = propValidation;

export const ClaimsByPetitionerLawfirmInstitutionOutcomeChart = props => (
  <ChartWithControls
    apiCall={getClaimsByPetitionerLawfirmInstitutionOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "Instituted" }}
    title="Institution Rates for Petitioners' Counsel"
    colorMap={COLOR_MAP}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_MORE_LEFT}
    legends={["Instituted", "Denied"]}
    {...props}
  />
);
ClaimsByPetitionerLawfirmInstitutionOutcomeChart.propTypes = propValidation;

export const ClaimsByPatentOwnerLawfirmInstitutionOutcomeChart = props => (
  <ChartWithControls
    apiCall={getClaimsByPatentOwnerLawfirmInstitutionOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "Denied" }}
    title="Institution Rates for Patent Owners' Counsel"
    colorMap={COLOR_MAP}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_MORE_LEFT}
    legends={["Instituted", "Denied"]}
    {...props}
  />
);
ClaimsByPatentOwnerLawfirmInstitutionOutcomeChart.propTypes = propValidation;

export const ClaimsByAuthorJudgeInstitutionOutcomeChart = props => (
  <ChartWithControls
    apiCall={getClaimsByAuthorJudgeInstitutionOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "__total__" }}
    title="Institution Rates for Author Judge"
    colorMap={COLOR_MAP}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_LEFT}
    legends={["Instituted", "Denied"]}
    {...props}
  />
);
ClaimsByAuthorJudgeInstitutionOutcomeChart.propTypes = propValidation;

export const ClaimsByPanelJudgeInstitutionOutcomeChart = props => (
  <ChartWithControls
    apiCall={getClaimsByPanelJudgeInstitutionOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "__total__" }}
    title="Institution Rates for Panel Judge"
    colorMap={COLOR_MAP}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_LEFT}
    legends={["Instituted", "Denied"]}
    {...props}
  />
);
ClaimsByPanelJudgeInstitutionOutcomeChart.propTypes = propValidation;

export const ClaimsByPetitionerLawfirmFWDOutcomeChart = props => (
  <ChartWithControls
    apiCall={getClaimsByPetitionerLawfirmFWDOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "Unpatentable" }}
    title="Final Written Decision Rates for Petitioners' Counsel"
    colorMap={COLOR_MAP}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_MORE_LEFT}
    legends={["Patentable", "Unpatentable", "Cancelled", "Settled", "Pending"]}
    {...props}
  />
);
ClaimsByPetitionerLawfirmFWDOutcomeChart.propTypes = propValidation;

export const ClaimsByPatentOwnerLawfirmFWDOutcomeChart = props => (
  <ChartWithControls
    apiCall={getClaimsByPatentOwnerLawfirmFWDOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "Patentable" }}
    title="Final Written Decision Rates for Patent Owners' Counsel"
    colorMap={COLOR_MAP}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_MORE_LEFT}
    legends={["Patentable", "Unpatentable", "Cancelled", "Settled", "Pending"]}
    {...props}
  />
);
ClaimsByPatentOwnerLawfirmFWDOutcomeChart.propTypes = propValidation;

export const ClaimsByAuthorJudgeFWDOutcomeChart = props => (
  <ChartWithControls
    apiCall={getClaimsByAuthorJudgeFWDOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "__total__" }}
    title="Final Written Decision Rates for Author Judge"
    colorMap={COLOR_MAP}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_LEFT}
    legends={["Patentable", "Unpatentable", "Cancelled", "Settled", "Pending"]}
    {...props}
  />
);
ClaimsByAuthorJudgeFWDOutcomeChart.propTypes = propValidation;

export const ClaimsByPanelJudgeFWDOutcomeChart = props => (
  <ChartWithControls
    apiCall={getClaimsByPanelJudgeFWDOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "__total__" }}
    title="Final Written Decision Rates for Panel Judge"
    colorMap={COLOR_MAP}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_LEFT}
    legends={["Patentable", "Unpatentable", "Cancelled", "Settled", "Pending"]}
    {...props}
  />
);
ClaimsByPanelJudgeFWDOutcomeChart.propTypes = propValidation;
